import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const LineContent = ({ lineData, selectedWords, onWordSelect, assignedGlossaryTerms = [] }) => {
  const [hoveredWord, setHoveredWord] = useState(null);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });

  const words = lineData?.text?.split(' ');

  const handleWordClick = (word, index) => {
    const wordKey = `${word}-${index}`;
    if (selectedWords?.includes(wordKey)) {
      onWordSelect(selectedWords?.filter(w => w !== wordKey));
    } else {
      onWordSelect([...selectedWords, wordKey]);
    }
  };

  const handleWordHover = (word, index, event) => {
    const cleanWord = word?.toLowerCase()?.replace(/[.,!?;:]/g, '');
    const glossaryTerm = assignedGlossaryTerms?.find(
      term => term?.word?.toLowerCase() === cleanWord
    );
    
    if (glossaryTerm) {
      const rect = event?.target?.getBoundingClientRect();
      setPreviewPosition({
        x: rect?.left + rect?.width / 2,
        y: rect?.top - 10
      });
      setHoveredWord({ word: cleanWord, term: glossaryTerm, index });
    }
  };

  const handleWordLeave = () => {
    setHoveredWord(null);
  };

  return (
    <div className="p-6 bg-background">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="User" size={20} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{lineData?.character}</p>
            <p className="text-xs text-muted-foreground">{lineData?.lineId}</p>
          </div>
        </div>
        {lineData?.stageDirection && (
          <div className="mb-3 p-3 bg-muted/50 rounded-md border-l-4 border-secondary">
            <p className="text-sm italic text-muted-foreground">
              [{lineData?.stageDirection}]
            </p>
          </div>
        )}
      </div>
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="MessageSquare" size={20} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-foreground mb-2">
              Line Text - Click Words for Glossary
            </h3>
            <div className="bg-accent/10 border-l-4 border-accent p-3 rounded">
              <p className="text-sm text-foreground leading-relaxed">
                <Icon name="Info" size={14} className="inline-block mr-1.5 text-accent" />
                Click any word below to select it for glossary assignment. Selected words appear with a colored background.
              </p>
            </div>
          </div>
        </div>

        <div className="p-8 rounded-lg border-2 border-primary/30 shadow-sm relative">
          <div className="flex flex-wrap gap-2">
            {words?.map((word, index) => {
              const wordKey = `${word}-${index}`;
              const isSelected = selectedWords?.includes(wordKey);
              const cleanWord = word?.toLowerCase()?.replace(/[.,!?;:]/g, '');
              const hasGlossary = assignedGlossaryTerms?.some(
                term => term?.word?.toLowerCase() === cleanWord
              );
              
              return (
                <span
                  key={wordKey}
                  onClick={() => handleWordClick(word, index)}
                  onMouseEnter={(e) => handleWordHover(word, index, e)}
                  onMouseLeave={handleWordLeave}
                  className={`
                    inline-flex items-center gap-1
                    cursor-pointer transition-all duration-200
                    px-3 py-2 rounded-md
                    text-base font-serif
                    select-none
                    ${
                      isSelected
                        ? 'bg-secondary text-secondary-foreground font-bold shadow-md scale-105 ring-2 ring-secondary/50'
                        : hasGlossary
                        ? 'bg-primary/5 border-2 border-primary/40 hover:bg-primary/15 hover:border-primary/60 hover:shadow-sm'
                        : 'bg-card border-2 border-border hover:bg-muted hover:border-primary/30 hover:shadow-sm'
                    }
                  `}
                >
                  <span>{word}</span>
                  {hasGlossary && (
                    <Icon 
                      name="BookOpen" 
                      size={12} 
                      className="text-primary flex-shrink-0" 
                    />
                  )}
                </span>
              );
            })}
          </div>

          {/* Glossary Preview Card */}
          {hoveredWord && (
            <div
              className="fixed z-50 transform -translate-x-1/2 -translate-y-full mb-2"
              style={{
                left: `${previewPosition?.x}px`,
                top: `${previewPosition?.y}px`,
                maxWidth: '320px'
              }}
            >
              <div className="bg-card border-2 border-primary shadow-xl rounded-lg p-4 animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div className="flex items-start gap-2 mb-2">
                  <Icon name="BookOpen" size={16} className="text-primary mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-foreground capitalize">
                      {hoveredWord?.term?.word}
                    </h4>
                    {hoveredWord?.term?.pronunciation && (
                      <p className="text-xs text-muted-foreground italic">
                        {hoveredWord?.term?.pronunciation}
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-foreground leading-relaxed">
                  {hoveredWord?.term?.definition}
                </p>
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-card border-r-2 border-b-2 border-primary"></div>
              </div>
            </div>
          )}
        </div>

        {selectedWords?.length > 0 && (
          <div className="mt-4 p-4 bg-success/10 border-2 border-success/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
                <Icon name="CheckCircle" size={18} className="text-success" />
              </div>
              <p className="text-sm text-foreground font-medium">
                <span className="font-bold text-success text-lg">{selectedWords?.length}</span> word(s) selected for glossary assignment
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LineContent;